import * as React from "react";

import _ from "lodash";
import { connect } from "react-redux";

import { DateTime } from "luxon";
import { Modal, Tooltip } from "@mantine/core";
import { IState } from "../../data/reducers/state";
import { IPerson } from "../../types/persons/person";
import { IShiftSession } from "../../types/shifts/Shift";
import { IShiftSessionEnrolment } from "../../types/shifts/ShiftEnrolment";
import { toDateTime } from "../../util/date+helper";
import { SSTable } from "../common/TableData";
import RosterPersonMoreInfo from "../management/roster/RosterPersonMoreInfo";

interface IShiftAvailabilitiesVisualiserProps {
  shiftSession: IShiftSession;
  enrolments: IShiftSessionEnrolment[];
  personMap: { [personId: string]: IPerson };
  associationId?: string;
}

interface PersonComputedEnrolments {
  person: string;
  avail: ComputedAvail[];
}

interface ComputedAvail {
  a: boolean;
  s: number;
  f: number;
  c?: string;
}

const ShiftAvailabilitiesVisualiser: React.FunctionComponent<
  IShiftAvailabilitiesVisualiserProps
> = (props) => {
  const [personIdMoreInfo, setPersonIdMoreInfo] = React.useState<string | null>(
    null,
  );

  const start = toDateTime(props.shiftSession.start);
  const finish = toDateTime(props.shiftSession.finish);

  const blockSizeMins = 5;
  const numberOfBlocks = Math.ceil(
    finish.diff(start, "minutes").minutes / blockSizeMins,
  );

  const tCellWidth = `calc(${100 / numberOfBlocks}% - 50px)`;

  const blockTimes: (DateTime | undefined)[] = Array.from(
    Array(numberOfBlocks),
  );
  blockTimes[0] = start;

  for (let block = 1; block < blockTimes.length; block++) {
    const blockDate = start.plus({ minutes: block * blockSizeMins });
    if (numberOfBlocks <= 100) {
      if (blockDate.minute % 30 == 0) {

        blockTimes[block] = blockDate;
      }
    } else if (blockDate.minute == 0) {
      blockTimes[block] = blockDate;
    }
  }

  const computedEnrolments: PersonComputedEnrolments[] = [];
  for (const enrolment of props.enrolments) {
    const computedEnrolmentTableBlocks: ComputedAvail[] = [];

    let previousEnd: number | undefined;

    for (const avail of enrolment.availability) {
      let availBlockStart: number;
      let availBlockFinish: number;
      const availStart = toDateTime(avail.start);
      const availFinish = toDateTime(avail.finish);

      if (availStart < start) {
        // range overlaps the start
        availBlockStart = 0;
        availBlockFinish = Math.ceil(
          availFinish.diff(start, "minutes").minutes / blockSizeMins,
        );
      } else if (availFinish > finish) {
        // range overlaps the end
        availBlockStart = Math.ceil(
          availStart.diff(start, "minutes").minutes / blockSizeMins,
        );
        availBlockFinish =
          numberOfBlocks -
          1 -
          Math.ceil(
            finish.diff(availFinish, "minutes").minutes / blockSizeMins,
          );
      } else {
        // range falls in the middle
        availBlockStart = Math.ceil(
          availStart.diff(start, "minutes").minutes / blockSizeMins,
        );
        availBlockFinish =
          numberOfBlocks -
          1 -
          Math.ceil(
            finish.diff(availFinish, "minutes").minutes / blockSizeMins,
          );
      }

      if (
        availBlockStart > 0 &&
        (!previousEnd || previousEnd < availBlockStart)
      ) {
        computedEnrolmentTableBlocks.push({
          a: false,
          s: previousEnd || 0,
          f: availBlockStart,
        });
      }
      availBlockStart = Math.max(0, availBlockStart);
      availBlockFinish = Math.min(numberOfBlocks - 1, availBlockFinish);
      computedEnrolmentTableBlocks.push({
        a: true,
        s: availBlockStart,
        f: availBlockFinish,
        c: avail.comment,
      });
      previousEnd = availBlockFinish;
    }

    if (!previousEnd || previousEnd < numberOfBlocks - 1) {
      computedEnrolmentTableBlocks.push({
        a: false,
        s: previousEnd || 0,
        f: numberOfBlocks - 1,
      });
    }
    computedEnrolments.push({
      person: enrolment.person,
      avail: computedEnrolmentTableBlocks,
    });
  }

  const sortedEnrolments = _.orderBy(
    computedEnrolments,
    (c) => c.avail.filter((a) => a.a)[0]?.s ?? numberOfBlocks + 1,
  );

  return (
    <>
      <SSTable>
        <thead>
          <tr>
            <th style={{ width: "100px" }} />
            {blockTimes.map((date, idx) => (
              <th
                style={{
                  width: `${tCellWidth}`,
                  padding: "0.5rem 0.1rem 0.5rem 0",
                }}
                key={`avail-time-header-${idx}`}
              >
                {date && date.toLocaleString(DateTime.TIME_SIMPLE)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedEnrolments.map((en) => (
            <tr key={`avail-row-${en.person}`}>
              <td style={{ padding: "0.1rem", minWidth: "50px" }}>
                <button
                  className="btn btn-link"
                  onClick={() => setPersonIdMoreInfo(en.person)}
                  style={{
                    padding: "0px",
                    color: "var(--color-text-primary)",
                    textDecoration: "none",
                  }}
                >
                  {props.personMap[en.person]?.fullName}
                </button>
              </td>
              {en.avail.map((computedAvail, idx) => {
                const key = `computed-enrolment-${en.person}-${idx}`;
                return (
                  <EnrolmentTableCell
                    key={key}
                    computedAvail={computedAvail}
                    id_={key}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </SSTable>
      {personIdMoreInfo && (
        <Modal
          opened={!!personIdMoreInfo}
          onClose={() => setPersonIdMoreInfo(null)}
          size="lg"
        >
          <RosterPersonMoreInfo
            person={props.personMap[personIdMoreInfo]}
            association={props.association}
            shiftContext={props.shiftSession.shift}
            relevantTime={toDateTime(props.shiftSession.start)}
          />
        </Modal>
      )}
    </>
  );
};

const EnrolmentTableCell: React.FunctionComponent<{
  computedAvail: ComputedAvail;
  id_: string;
}> = ({ computedAvail, id_ }) => {
  return (
    <td
      id={id_}
      colSpan={computedAvail.f - computedAvail.s}
      style={{
        backgroundColor: computedAvail.a
          ? "var(--color-check-green)"
          : "var(--official-cell-bg-rejectedAppointment)",
      }}
    >
      {computedAvail.c ? (
        <Tooltip label={computedAvail.c} withArrow>
          <span style={{ display: "block", width: "100%", height: "100%" }} />
        </Tooltip>
      ) : (
        <span style={{ display: "block", width: "100%", height: "100%" }} />
      )}
    </td>
  );
};

function mapStateToProps(
  state: IState,
  props: IShiftAvailabilitiesVisualiserProps,
): Partial<IShiftAvailabilitiesVisualiserProps> {
  return {
    personMap: state.persons.map,
  };
}

export default connect(mapStateToProps)(ShiftAvailabilitiesVisualiser);
